import {
  Alert, Box, Button, Card, CardContent, Chip, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Save, Send, Lock } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, extractError } from '../../../shared/api';
import type { Class, ExamType, Mark, Student, Subject } from '../../../shared/types';
import { useAuthStore } from '../../../features/auth/store';
import PageHeader from '../../../shared/PageHeader';
import { gradeFromPercentage } from '../../../shared/utils';

export default function MarksPage() {
  const user = useAuthStore((s) => s.user);
  if (user?.role === 'STUDENT') return <StudentMarksView />;
  return <TeacherMarksEntry />;
}

function TeacherMarksEntry() {
  const [classId, setClassId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [examTypeId, setExamTypeId] = useState('');
  const [entries, setEntries] = useState<Record<string, { marks: number | null; max: number }>>({});
  const qc = useQueryClient();

  const classesQ = useQuery({ queryKey: ['classes'], queryFn: async () => (await api.get<Class[]>('/classes')).data });
  const subjectsQ = useQuery({ queryKey: ['subjects'], queryFn: async () => (await api.get<Subject[]>('/subjects')).data });
  const examTypesQ = useQuery({ queryKey: ['exam-types'], queryFn: async () => (await api.get<ExamType[]>('/exam-types')).data });
  const studentsQ = useQuery({
    queryKey: ['students-by-class', classId],
    queryFn: async () => (await api.get<Student[]>('/admin/students', { params: { classId } })).data,
    enabled: !!classId,
  });
  const marksQ = useQuery({
    queryKey: ['marks', classId, subjectId, examTypeId],
    queryFn: async () => (await api.get<Mark[]>('/marks', { params: { classId, subjectId, examTypeId } })).data,
    enabled: !!classId && !!subjectId && !!examTypeId,
  });

  useEffect(() => {
    const map: typeof entries = {};
    (studentsQ.data ?? []).forEach((s) => {
      const m = marksQ.data?.find((x) => x.studentId === s.id);
      map[s.id] = { marks: m?.marksObtained ?? null, max: m?.maxMarks ?? 100 };
    });
    setEntries(map);
  }, [studentsQ.data, marksQ.data]);

  const lockedCount = (marksQ.data ?? []).filter((m) => m.status === 'PUBLISHED').length;
  const submittedCount = (marksQ.data ?? []).filter((m) => m.status === 'SUBMITTED').length;
  const isLocked = lockedCount > 0;
  const isSubmitted = !isLocked && submittedCount > 0;

  const saveDraft = useMutation({
    mutationFn: async () => {
      const list = Object.entries(entries).map(([studentId, v]) => ({
        studentId, marksObtained: v.marks, maxMarks: v.max,
      }));
      return (await api.put('/marks/bulk', { classId, subjectId, examTypeId, sessionId: 'sess_2025_26', entries: list })).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marks'] }),
  });

  const submit = useMutation({
    mutationFn: async () => (await api.post('/marks/submit', { classId, subjectId, examTypeId, sessionId: 'sess_2025_26' })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['marks'] }); qc.invalidateQueries({ queryKey: ['notifications'] }); },
  });

  return (
    <Box>
      <PageHeader title="Marks Entry" subtitle="Update marks for assigned class and subject. Submit for principal approval." />

      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField select label="Class" value={classId} onChange={(e) => setClassId(e.target.value)} sx={{ minWidth: 180 }}>
            {(classesQ.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name}-{c.section}</MenuItem>)}
          </TextField>
          <TextField select label="Subject" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} sx={{ minWidth: 180 }}>
            {(subjectsQ.data ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
          <TextField select label="Exam" value={examTypeId} onChange={(e) => setExamTypeId(e.target.value)} sx={{ minWidth: 180 }}>
            {(examTypesQ.data ?? []).map((e) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
          </TextField>
        </Stack>
      </Card>

      {!classId || !subjectId || !examTypeId ? (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">Select a class, subject and exam to begin.</Typography>
        </Card>
      ) : (
        <>
          <Stack direction={{ sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1}>
              {isLocked && <Chip color="success" icon={<Lock />} label="Published — locked" />}
              {isSubmitted && <Chip color="info" label="Submitted to Principal" />}
              {!isLocked && !isSubmitted && <Chip color="warning" label="Draft" />}
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" startIcon={<Save />} onClick={() => saveDraft.mutate()} disabled={isLocked || saveDraft.isPending}>
                {saveDraft.isPending ? 'Saving…' : 'Save draft'}
              </Button>
              <Button variant="contained" startIcon={<Send />} onClick={() => submit.mutate()} disabled={isLocked || isSubmitted || submit.isPending}>
                {submit.isPending ? 'Submitting…' : 'Submit to Principal'}
              </Button>
            </Stack>
          </Stack>
          {(saveDraft.isSuccess || submit.isSuccess) && <Alert severity="success" sx={{ mb: 2 }}>Saved.</Alert>}
          {(saveDraft.isError || submit.isError) && <Alert severity="error" sx={{ mb: 2 }}>{extractError(saveDraft.error ?? submit.error)}</Alert>}

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Roll</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Marks</TableCell>
                  <TableCell>Max</TableCell>
                  <TableCell>%</TableCell>
                  <TableCell>Grade</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(studentsQ.data ?? []).map((s) => {
                  const e = entries[s.id] ?? { marks: null, max: 100 };
                  const pct = e.marks != null && e.max ? +(e.marks / e.max * 100).toFixed(1) : null;
                  return (
                    <TableRow key={s.id}>
                      <TableCell sx={{ fontWeight: 700 }}>{s.rollNumber}</TableCell>
                      <TableCell>{s.firstName} {s.lastName}</TableCell>
                      <TableCell sx={{ width: 120 }}>
                        <TextField
                          type="number" size="small" value={e.marks ?? ''}
                          disabled={isLocked}
                          onChange={(ev) => setEntries((prev) => ({
                            ...prev, [s.id]: { ...prev[s.id], marks: ev.target.value === '' ? null : Number(ev.target.value) },
                          }))}
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ width: 80 }}>
                        <TextField
                          type="number" size="small" value={e.max} disabled={isLocked}
                          onChange={(ev) => setEntries((prev) => ({ ...prev, [s.id]: { ...prev[s.id], max: Number(ev.target.value) } }))}
                          inputProps={{ min: 1 }}
                        />
                      </TableCell>
                      <TableCell>{pct != null ? `${pct}%` : '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>{pct != null ? gradeFromPercentage(pct) : '—'}</TableCell>
                    </TableRow>
                  );
                })}
                {(studentsQ.data ?? []).length === 0 && (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No students in this class.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

function StudentMarksView() {
  const examTypesQ = useQuery({ queryKey: ['exam-types'], queryFn: async () => (await api.get<ExamType[]>('/exam-types')).data });
  const subjectsQ = useQuery({ queryKey: ['subjects'], queryFn: async () => (await api.get<Subject[]>('/subjects')).data });
  const marksQ = useQuery({ queryKey: ['marks-me'], queryFn: async () => (await api.get<Mark[]>('/marks')).data });

  const subjMap = useMemo(() => new Map((subjectsQ.data ?? []).map((s) => [s.id, s.name])), [subjectsQ.data]);
  const grouped = useMemo(() => {
    const map: Record<string, Mark[]> = {};
    (marksQ.data ?? []).forEach((m) => {
      (map[m.examTypeId] ??= []).push(m);
    });
    return map;
  }, [marksQ.data]);

  return (
    <Box>
      <PageHeader title="My Results" subtitle="Your published marks across all exams." />
      {(examTypesQ.data ?? []).map((ex) => {
        const list = grouped[ex.id] ?? [];
        if (list.length === 0) return null;
        const total = list.reduce((s, m) => s + (m.marksObtained ?? 0), 0);
        const max = list.reduce((s, m) => s + m.maxMarks, 0);
        const pct = max ? +(total / max * 100).toFixed(2) : 0;
        return (
          <Card key={ex.id} sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction={{ sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{ex.name}</Typography>
                <Stack direction="row" spacing={1}>
                  <Chip label={`${total} / ${max}`} />
                  <Chip color="primary" label={`${pct}%`} />
                  <Chip color="secondary" label={gradeFromPercentage(pct)} />
                </Stack>
              </Stack>
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead><TableRow><TableCell>Subject</TableCell><TableCell align="right">Marks</TableCell><TableCell align="right">%</TableCell><TableCell align="right">Grade</TableCell></TableRow></TableHead>
                <TableBody>
                  {list.map((m) => {
                    const p = m.marksObtained != null && m.maxMarks ? +(m.marksObtained / m.maxMarks * 100).toFixed(1) : 0;
                    return (
                      <TableRow key={m.id}>
                        <TableCell>{subjMap.get(m.subjectId)}</TableCell>
                        <TableCell align="right">{m.marksObtained ?? '—'} / {m.maxMarks}</TableCell>
                        <TableCell align="right">{p}%</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{gradeFromPercentage(p)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}
      {(marksQ.data ?? []).length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No published results yet.</Typography>
        </Paper>
      )}
    </Box>
  );
}
